/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jan 12 2024
 **/

import { useCallback } from "react";
import { atom, useAtomValue } from "jotai";
import { atomFamily, useAtomCallback } from "jotai/utils";

import { JotaiStore } from "@/frameworks/libraries/commons";
import { Properties } from "@/frameworks/libraries/properties";
import { useRoomViewActions } from "../useRoomView";

type MiniChatType = {
	id: string;
	type: ChatServices.RoomType;
	spaceID?: string;
	startCoordinate: { x: number; y: number };
	zIndex: number;
	focus?: boolean;
	ignoreUpdate?: boolean;
	size?: { width?: number; height?: number };
	threadID?: string;
};

const miniChatPopupListAtom = atom<MiniChatType[]>([]);
const miniChatPopupListAtomFamily = atomFamily(
	({ id, spaceID, threadID }: { id: string; spaceID?: string; threadID?: string }) => {
		return atom((get) => {
			return get(miniChatPopupListAtom).find((v) => {
				if (threadID) {
					return v.id === id && v.threadID === threadID;
				}
				if (spaceID) {
					return v.id === id && v.spaceID === spaceID;
				}
				return v.id === id;
			});
		});
	},
	(prev, next) => {
		return (
			prev.id === next.id &&
			(prev.spaceID ?? null) === (next.spaceID ?? null) &&
			(prev.threadID ?? null) === (next.threadID ?? null)
		);
	}
);

export default function useMiniChatPopupManager() {
	const { updateRoomView } = useRoomViewActions();

	const getAll = useAtomCallback(
		useCallback((get) => {
			return get(miniChatPopupListAtom);
		}, []),
		{ store: JotaiStore }
	);

	const get = useAtomCallback(
		useCallback((get, _, { id, spaceID }: { id: string; spaceID?: string }) => {
			return get(miniChatPopupListAtom).find((v) => {
				if (spaceID) {
					return v.id === id && v.spaceID === spaceID;
				}
				return v.id === id;
			});
		}, []),
		{ store: JotaiStore }
	);

	const getLargestZIndex = useAtomCallback(
		useCallback((get) => {
			const zIndexList = get(miniChatPopupListAtom).map((v) => v.zIndex);
			return Math.max(...zIndexList);
		}, []),
		{ store: JotaiStore }
	);

	const show = useAtomCallback(
		useCallback((_, set, miniChatData: MiniChatType) => {
			if (!miniChatData.ignoreUpdate) {
				if (!miniChatData.id) return;
				updateRoomView({ roomId: miniChatData.id, deviceKey: Properties.browserTabID, view: true });
			}

			set(miniChatPopupListAtom, (prev) => {
				const result = [...prev].map((v) => ({ ...v, focus: false }));
				result.push({ ...miniChatData, focus: true });
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const hide = useAtomCallback(
		useCallback((_, set, id: string, ignoreUpdate: boolean = false) => {
			if (id && !ignoreUpdate) {
				updateRoomView({ roomId: id, deviceKey: Properties.browserTabID, view: false });
			}
			set(miniChatPopupListAtom, (prev) => {
				const largestZIndex = getLargestZIndex();
				const result = [...prev].filter((v) => v.id !== id);

				return result.map((v) => {
					if (v.zIndex === largestZIndex - 1) {
						return { ...v, focus: true };
					}
					return v;
				});
			});
		}, []),
		{ store: JotaiStore }
	);

	const getCurrentFocus = useAtomCallback(
		useCallback((get) => {
			const largestZIndex = getLargestZIndex();
			const miniChatPopupList = get(miniChatPopupListAtom);
			return miniChatPopupList.find((v) => v.zIndex === largestZIndex);
		}, []),
		{ store: JotaiStore }
	);

	const focus = useAtomCallback(
		useCallback((_, set, id: string) => {
			if (getCurrentFocus()?.id === id) {
				return;
			}
			set(miniChatPopupListAtom, (prev) => {
				const target = prev.find((v) => v.id === id);
				if (!target) return prev;

				const largestZIndex = getLargestZIndex();
				return prev.map((v) => {
					if (v.id === id) return { ...v, zIndex: largestZIndex, focus: true };
					else if (v.zIndex >= target.zIndex) return { ...v, zIndex: v.zIndex - 1, focus: false };
					return { ...v, focus: false };
				});
			});
		}, []),
		{ store: JotaiStore }
	);

	const clear = useAtomCallback(
		useCallback((_, set) => {
			set(miniChatPopupListAtom, []);
		}, []),
		{ store: JotaiStore }
	);

	const checkIsOpen = useAtomCallback(
		useCallback((get, _, id: string, threadID?: string) => {
			if (threadID) return get(miniChatPopupListAtom).some((v) => v.id === id && v.threadID === threadID);
			else return get(miniChatPopupListAtom).some((v) => v.id === id);
		}, []),
		{ store: JotaiStore }
	);

	const miniChatPopupActions = {
		getAll,
		get,
		show,
		hide,
		clear,
		focus,
		getCurrentFocus,
		checkIsOpen
	};

	return {
		miniChatPopupActions,
		useMiniChatListRenderer() {
			return useAtomValue(miniChatPopupListAtom, { store: JotaiStore });
		},
		miniChat: (id: string, spaceID?: string, threadID?: string) =>
			useAtomValue(miniChatPopupListAtomFamily({ id, spaceID, threadID }), { store: JotaiStore })
	};
}
